import React, { type ReactElement } from 'react';
import { selectCartItems, useCartStore } from '@/store/use-cart.store';
import { type CalculationResponseType } from '@/common/apis/cart/basket-calculation';
import LabelValue from '@/common/components/label-value';
import Price from '@/common/components/price';
import SkeletonBuilder from '@/common/components/skeleton-builder/skeleton-builder.component';
import Text from '@/common/components/text';
import Wrapper from '@/common/components/wrapper';
import { FeatureFlag } from '@/common/constants/feature-flag.enum';
import { useFeatureFlag } from '@/common/hooks/use-feature-flag.hook';

export interface DetailsProps {
  calculation: CalculationResponseType;
  quantity: number;
  pending?: boolean;
}

function MoneyValue({
  pending,
  price,
}: {
  pending?: boolean;
  price: number;
}): ReactElement {
  if (pending) return <SkeletonBuilder type="text" />;
  return (
    <Price
      currencyClassName="!text-black-300 !text-sm !font-normal"
      priceClassName="!flex-row !text-sm !font-semibold !text-black-default"
      price={price}
    />
  );
}

function ShippingFeeValue({
  pending,
  fee,
}: {
  pending?: boolean;
  fee: number;
}): ReactElement {
  if (pending) return <SkeletonBuilder type="text" />;
  if (fee > 0) {
    return (
      <Price
        currencyClassName="!text-black-300 !text-sm !font-normal"
        priceClassName="!flex-row !text-sm !font-semibold !text-black-default"
        price={fee}
      />
    );
  }
  if (fee === 0) {
    return (
      <Text className="font-medium lg:!text-sm lg:!text-black-300 !text-black-400">
        رایگان
      </Text>
    );
  }
  return (
    <div className="flex items-center gap-x-1">
      <Text className="font-medium lg:!text-sm lg:!text-black-default ">
        پس کرایه
      </Text>
      <Text className="text-xs font-normal lg:!text-[#45678D]">
        (به عهده خریدار)
      </Text>
    </div>
  );
}

export default function Details({
  calculation,
  quantity,
  pending,
}: DetailsProps) {
  const { totalAmount, totalAmountPayable, totalDiscount, totalShippingFee } =
    calculation;
  const shippingMethodsEnabled = useFeatureFlag(FeatureFlag.ShippingMethods);
  const localCartItems = useCartStore(selectCartItems);
  const products = localCartItems[0]?.products;

  const taxPercent = calculation.taxPercent || 0;
  const priceAfterDiscount = totalAmount - totalDiscount;
  const taxAmount = Math.round(priceAfterDiscount * (taxPercent / 100));

  // Multi-method shipping is chosen on the invoice page — strip any legacy fee
  // from the cart payable so shoppers do not see the old flat amount.
  const showLegacyShipping = !shippingMethodsEnabled;
  const displayPayable =
    shippingMethodsEnabled && totalShippingFee > 0
      ? totalAmountPayable - totalShippingFee
      : totalAmountPayable;

  return (
    <Wrapper className="!h-auto px-4 lg:mx-[24px] lg:!mt-[24px] lg:!w-auto lg:!px-0 pb-6">
      <div className="hidden lg:!block">
        <div className="flex w-full flex-row items-center justify-between text-base font-medium text-[#748DA9] ">
          <h1> جزییات سبد</h1>
          {products && <h2> {quantity} محصول</h2>}
        </div>
      </div>
      <LabelValue className="mt-3">
        <LabelValue.Label className="!text-sm !font-medium !text-black-default">
          قیمت کالاها:
        </LabelValue.Label>
        <LabelValue.Value>
          <MoneyValue pending={pending} price={totalAmount} />
        </LabelValue.Value>
      </LabelValue>
      {totalDiscount > 0 && (
        <LabelValue className="mt-3">
          <LabelValue.Label className="!text-sm !font-medium !text-black-default">
            سود شما از خرید:
          </LabelValue.Label>
          <LabelValue.Value>
            <MoneyValue pending={pending} price={totalDiscount} />
          </LabelValue.Value>
        </LabelValue>
      )}

      {totalDiscount > 0 && (
        <LabelValue className="mt-3">
          <LabelValue.Label className="!text-sm !font-medium !text-black-default">
            قیمت کالاها پس از تخفیف:
          </LabelValue.Label>
          <LabelValue.Value>
            <MoneyValue pending={pending} price={priceAfterDiscount} />
          </LabelValue.Value>
        </LabelValue>
      )}

      {taxPercent > 0 && (
        <LabelValue className="mt-3">
          <LabelValue.Label className="!text-sm !font-medium !text-black-default">
            مالیات بر ارزش افزوده ({taxPercent}%):
          </LabelValue.Label>
          <LabelValue.Value>
            <MoneyValue pending={pending} price={taxAmount} />
          </LabelValue.Value>
        </LabelValue>
      )}

      {showLegacyShipping ? (
        <LabelValue className="mt-3">
          <LabelValue.Label className="!text-sm !font-medium !text-black-default">
            هزینه ارسال:
          </LabelValue.Label>
          <LabelValue.Value>
            <ShippingFeeValue pending={pending} fee={totalShippingFee} />
          </LabelValue.Value>
        </LabelValue>
      ) : null}

      <LabelValue className="mt-3">
        <LabelValue.Label className="!text-sm !font-medium !text-black-default">
          قابل پرداخت:
        </LabelValue.Label>
        <LabelValue.Value>
          <MoneyValue pending={pending} price={displayPayable} />
        </LabelValue.Value>
      </LabelValue>
    </Wrapper>
  );
}
